/**
 * 停點編輯器（接機多目的地 / 送機多上車點 / 點對點多停點）
 * 每個停點可「從清單選」（POI 選擇器）或「自行輸入」（location＋必選 zone）；
 * 可新增、刪除、上下移排序。PoiPickerDialog 另供包車上下車點快選使用。
 */
import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ListPlus, MapPin, Plus, Search, X } from "lucide-react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { Button } from "../../ui/button";
import { Input, Select } from "../../ui/input";
import { Badge } from "../../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import type { Loc, Region } from "../../../../../shared/const";
import { POI_CATEGORIES, ZONES, type PoiCategory, type ZoneCode } from "../../../../../shared/booking";
import { REGION_LABEL, emptyStop, zoneLabel, type DraftStop, type PoiOption } from "./wizard-shared";

const CATEGORY_LABEL: Record<PoiCategory, Loc> = {
  hotel: { "zh-TW": "飯店", "en-US": "Hotels", "zh-CN": "饭店" },
  attraction: { "zh-TW": "景點", "en-US": "Attractions", "zh-CN": "景点" },
  transport: { "zh-TW": "交通樞紐", "en-US": "Transport", "zh-CN": "交通枢纽" },
};

/* ── POI 選擇器 ─────────────────────────────────────────────── */

export function PoiPickerDialog({
  open,
  onOpenChange,
  pois,
  region,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pois: PoiOption[];
  /** 過濾 region；null = 不限（台灣/香港皆可選） */
  region: Region | null;
  onSelect: (poi: PoiOption) => void;
}) {
  const { t } = useLanguage();
  const [category, setCategory] = useState<PoiCategory>("hotel");
  const [search, setSearch] = useState("");

  // 開啟時重置草稿（預設 category=hotel、清空搜尋），避免殘留上一次的篩選
  const changeOpen = (o: boolean) => {
    if (o) {
      setCategory("hotel");
      setSearch("");
    }
    onOpenChange(o);
  };

  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return pois.filter(
      (p) =>
        (region === null || p.region === region) &&
        p.category === category &&
        (kw === "" || p.name.toLowerCase().includes(kw) || (p.address ?? "").toLowerCase().includes(kw)),
    );
  }, [pois, region, category, search]);

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      {/* 只是挑一個地點，關掉不會丟失建單表單的內容，維持點外面即關閉 */}
      <DialogContent dismissible className="max-h-[80vh] overflow-hidden sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t({ "zh-TW": "從清單選地點", "en-US": "Pick from list", "zh-CN": "从清单选地点" })}</DialogTitle>
          <DialogDescription>
            {region
              ? `${t(REGION_LABEL[region])} · ${t({ "zh-TW": "選擇飯店、景點或交通樞紐", "en-US": "Choose a hotel, attraction or transport hub", "zh-CN": "选择饭店、景点或交通枢纽" })}`
              : t({ "zh-TW": "選擇飯店、景點或交通樞紐", "en-US": "Choose a hotel, attraction or transport hub", "zh-CN": "选择饭店、景点或交通枢纽" })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {POI_CATEGORIES.map((c) => {
              const active = category === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  aria-pressed={active}
                  className={`rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ${
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-muted-foreground hover:border-border-strong hover:text-foreground"
                  }`}
                >
                  {t(CATEGORY_LABEL[c])}
                </button>
              );
            })}
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t({ "zh-TW": "搜尋名稱或地址…", "en-US": "Search name or address…", "zh-CN": "搜索名称或地址…" })}
            />
          </div>

          <div className="max-h-[44vh] space-y-1 overflow-y-auto pr-1">
            {filtered.length === 0 ? (
              <p className="p-6 text-center text-[13px] text-muted-foreground">
                {t({ "zh-TW": "沒有符合的地點", "en-US": "No matching places", "zh-CN": "没有符合的地点" })}
              </p>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    onSelect(p);
                    onOpenChange(false);
                  }}
                  className="group flex w-full items-start gap-3 rounded-md border border-transparent p-2.5 text-left transition-colors hover:border-border hover:bg-muted/50"
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary" strokeWidth={1.75} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium leading-tight">{p.name}</span>
                    {p.address && <span className="block truncate text-[12px] text-muted-foreground">{p.address}</span>}
                  </span>
                  <Badge variant="outline" className="shrink-0 text-[12px] normal-case tracking-normal">
                    {t(zoneLabel(p.zone as ZoneCode))}
                  </Badge>
                </button>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── 停點編輯器 ─────────────────────────────────────────────── */

export function StopsEditor({
  stops,
  onChange,
  pois,
  region,
  minStops,
  maxStops = 10,
  itemLabel,
  addLabel,
}: {
  stops: DraftStop[];
  onChange: (stops: DraftStop[]) => void;
  pois: PoiOption[];
  /** POI 與 zone 選項的 region 過濾；null = 不限 */
  region: Region | null;
  minStops: number;
  maxStops?: number;
  /** 停點稱呼（目的地/上車點/停點） */
  itemLabel: Loc;
  addLabel: Loc;
}) {
  const { t } = useLanguage();
  const [pickerFor, setPickerFor] = useState<number | null>(null);

  const zoneOptions = ZONES.filter((z) => region === null || z.region === region);

  const update = (i: number, patch: Partial<DraftStop>) =>
    onChange(stops.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= stops.length) return;
    const next = [...stops];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };

  return (
    <div className="space-y-2.5">
      {stops.map((s, i) => (
        <div key={i} className="rounded-md border border-border bg-card p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[12px] font-medium text-muted-foreground">
              <span className="numeric mr-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-soft text-[12px] font-semibold text-primary">
                {i + 1}
              </span>
              {t(itemLabel)} {stops.length > 1 ? i + 1 : ""}
            </p>
            <div className="flex items-center gap-0.5">
              {stops.length > 1 && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    aria-label={t({ "zh-TW": "上移", "en-US": "Move up", "zh-CN": "上移" })}
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    disabled={i === stops.length - 1}
                    onClick={() => move(i, 1)}
                    aria-label={t({ "zh-TW": "下移", "en-US": "Move down", "zh-CN": "下移" })}
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
              {stops.length > minStops && (
                <Button
                  type="button"
                  variant="ghost"
                  size="iconSm"
                  onClick={() => onChange(stops.filter((_, idx) => idx !== i))}
                  aria-label={t({ "zh-TW": "刪除", "en-US": "Remove", "zh-CN": "删除" })}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>

          {s.poiId !== null ? (
            <div className="mt-2 flex items-center justify-between gap-2 rounded-md bg-muted/50 px-3 py-2">
              <span className="flex min-w-0 items-center gap-2">
                <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={1.75} />
                <span className="truncate text-[13px] font-medium">{s.location}</span>
                <Badge variant="primary" className="shrink-0 text-[12px] normal-case tracking-normal">
                  {t(zoneLabel(s.zone))}
                </Badge>
              </span>
              <Button type="button" variant="ghost" size="sm" onClick={() => update(i, emptyStop())}>
                {t({ "zh-TW": "重選", "en-US": "Change", "zh-CN": "重选" })}
              </Button>
            </div>
          ) : (
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_150px_auto]">
              <Input
                value={s.location}
                onChange={(e) => update(i, { location: e.target.value })}
                placeholder={t({ "zh-TW": "自行輸入地址或地點名稱", "en-US": "Type an address or place", "zh-CN": "自行输入地址或地点名称" })}
              />
              <Select
                value={s.zone}
                onChange={(e) => update(i, { zone: e.target.value as ZoneCode | "" })}
                aria-label={t({ "zh-TW": "計價區域", "en-US": "Pricing zone", "zh-CN": "计价区域" })}
              >
                <option value="">
                  {t({ "zh-TW": "選擇區域（必選）", "en-US": "Zone (required)", "zh-CN": "选择区域（必选）" })}
                </option>
                {zoneOptions.map((z) => (
                  <option key={z.code} value={z.code}>
                    {t(z.label)}
                  </option>
                ))}
              </Select>
              <Button type="button" variant="outline" size="default" onClick={() => setPickerFor(i)}>
                <ListPlus className="h-3.5 w-3.5" />
                {t({ "zh-TW": "從清單選", "en-US": "From list", "zh-CN": "从清单选" })}
              </Button>
            </div>
          )}
          {s.poiId === null && s.location.trim() !== "" && s.zone === "" && (
            <p className="mt-1.5 text-[12px] text-destructive">
              {t({ "zh-TW": "自行輸入地點需補選計價區域。", "en-US": "Please pick a pricing zone for a typed-in location.", "zh-CN": "自行输入地点需补选计价区域。" })}
            </p>
          )}
        </div>
      ))}

      {stops.length < maxStops && (
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...stops, emptyStop()])}>
          <Plus className="h-3.5 w-3.5" />
          {t(addLabel)}
        </Button>
      )}

      <PoiPickerDialog
        open={pickerFor !== null}
        onOpenChange={(o) => setPickerFor(o ? pickerFor : null)}
        pois={pois}
        region={region}
        onSelect={(p) => {
          if (pickerFor !== null) {
            update(pickerFor, { location: p.name, zone: p.zone as ZoneCode, poiId: p.id });
          }
        }}
      />
    </div>
  );
}
